import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { getAllTransactions } from '../services/transactionService';
import { SalesTransaction } from '../types';
import { auth } from '../services/firebase';
import { QueryDocumentSnapshot } from 'firebase/firestore';

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
}

interface TransactionContextType {
  transactions: SalesTransaction[];
  loading: boolean;
  error: string | null;
  pagination: PaginationData;
  fetchTransactions: (page?: number, pageSize?: number) => Promise<void>;
  fetchNextPage: () => Promise<void>;
  fetchPrevPage: () => Promise<void>;
  dataLoaded: boolean;
}

const TransactionContext = createContext<TransactionContextType | undefined>(undefined);

export const useTransaction = (): TransactionContextType => {
  const context = useContext(TransactionContext);
  if (context === undefined) {
    throw new Error('useTransaction must be used within a TransactionProvider');
  }
  return context;
};

interface TransactionProviderProps {
  children: ReactNode;
}

export const TransactionProvider: React.FC<TransactionProviderProps> = ({ children }) => {
  const [transactions, setTransactions] = useState<SalesTransaction[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 15, // Default page size for transactions
  });
  const [lastVisibleDoc, setLastVisibleDoc] = useState<QueryDocumentSnapshot | undefined>(undefined);
  const [firstVisibleDocs, setFirstVisibleDocs] = useState<(QueryDocumentSnapshot | undefined)[]>([]);

  const fetchTransactions = useCallback(async (
    page: number = 1,
    pageSize: number = pagination.pageSize,
    direction: 'next' | 'prev' | 'jump' = 'jump'
  ) => {
    setLoading(true);
    setError(null);
    try {
      if (!auth?.currentUser) {
        throw new Error('Kullanıcı giriş yapmamış.');
      }

      let cursor: QueryDocumentSnapshot | undefined = undefined;
      if (direction === 'next') {
        cursor = lastVisibleDoc;
      } else if (direction === 'prev' && page > 0) {
        cursor = firstVisibleDocs[page -1];
      }
      // For 'jump', cursor remains undefined unless specific logic is added for direct page jumps with cursors

      const result = await getAllTransactions(pageSize, cursor, direction === 'prev' ? 'prev' : 'next');
      
      setTransactions(result.transactions);
      
      if (result.transactions.length > 0) {
        if (direction !== 'prev') {
            setLastVisibleDoc(result.lastVisibleDocument);
            const newFirstVisibleDocs = [...firstVisibleDocs];
            if(page > newFirstVisibleDocs.length) {
                 newFirstVisibleDocs[page-1] = result.firstVisibleDocument;
            } else {
                 newFirstVisibleDocs[page-1] = result.firstVisibleDocument;
            }
            setFirstVisibleDocs(newFirstVisibleDocs);
        } else if (page > 0) {
            // When going previous, the new lastVisible is the one before the current first.
            // The new firstVisible is the first of the fetched set.
            setLastVisibleDoc(firstVisibleDocs[page-1]); // The one we startedAfter or endedBefore
            const newFirstVisibleDocs = [...firstVisibleDocs];
            newFirstVisibleDocs[page-1] = result.firstVisibleDocument;
            setFirstVisibleDocs(newFirstVisibleDocs);
        }
      }

      // This pagination logic is an estimation as Firestore doesn't easily give total counts with cursor pagination.
      // For a more robust totalPages/totalItems, a separate count query would be needed or a different backend approach.
      const newPagination = {
        currentPage: page,
        pageSize: pageSize,
        totalItems: Math.max(pagination.totalItems, page * pageSize + (result.hasNextPage ? pageSize : 0)), // Keep the highest estimate
        totalPages: result.hasNextPage ? Math.max(page + 1, pagination.totalPages) : page,
      };

      setPagination(newPagination);
      setDataLoaded(true);
    } catch (err: any) {
      console.error('Error fetching transactions:', err);
      setError(err.message || 'İşlemler alınırken bir hata oluştu.');
      setDataLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, dataLoaded, transactions.length, pagination, lastVisibleDoc, firstVisibleDocs]);

  const fetchNextPage = useCallback(async () => {
    if (pagination.currentPage < pagination.totalPages || transactions.length === pagination.pageSize) { // Heuristic for hasNextPage
      await fetchTransactions(pagination.currentPage + 1, pagination.pageSize, 'next');
    }
  }, [fetchTransactions, pagination, transactions.length]);

  const fetchPrevPage = useCallback(async () => {
    if (pagination.currentPage > 1) {
      await fetchTransactions(pagination.currentPage - 1, pagination.pageSize, 'prev');
    }
  }, [fetchTransactions, pagination]);

  return (
    <TransactionContext.Provider value={{
      transactions,
      loading,
      error,
      pagination,
      fetchTransactions,
      fetchNextPage,
      fetchPrevPage,
      dataLoaded,
    }}>
      {children}
    </TransactionContext.Provider>
  );
};